import { useMemo } from "react";
import {
  deriveActiveActionRequired,
  deriveActivePlanState,
  derivePendingApprovals,
  derivePendingUserInputs,
  deriveWorkLogEntries,
  findLatestProposedPlan,
  findSidebarProposedPlan,
  hasActionableProposedPlan,
  hasToolActivityForTurn,
} from "../../session-logic";
import { type Thread } from "../../types";
import { type ThreadPlanCatalogEntry } from "../ChatView.logic";

const EMPTY_THREAD_ACTIVITIES: NonNullable<Thread["activities"]> = [];

export function useChatActivityViewState(input: {
  readonly activeThread: Thread | undefined;
  readonly activeLatestTurn: Thread["latestTurn"] | null;
  readonly latestTurnSettled: boolean;
  readonly interactionMode: string;
  readonly phase: string;
  readonly threadPlanCatalog: readonly ThreadPlanCatalogEntry[];
}): {
  readonly threadActivities: NonNullable<Thread["activities"]>;
  readonly workLogEntries: ReturnType<typeof deriveWorkLogEntries>;
  readonly latestTurnHasToolActivity: boolean;
  readonly pendingApprovals: ReturnType<typeof derivePendingApprovals>;
  readonly pendingUserInputs: ReturnType<typeof derivePendingUserInputs>;
  readonly activeProposedPlan: ReturnType<typeof findLatestProposedPlan>;
  readonly sidebarProposedPlan: ReturnType<typeof findSidebarProposedPlan>;
  readonly activePlan: ReturnType<typeof deriveActivePlanState>;
  readonly planSidebarLabel: "Plan" | "Tasks";
  readonly showPlanFollowUpPrompt: boolean;
  readonly activePendingApproval: ReturnType<typeof derivePendingApprovals>[number] | null;
  readonly activeActionRequired: ReturnType<typeof deriveActiveActionRequired>;
} {
  const { activeLatestTurn, activeThread, interactionMode, latestTurnSettled, phase } = input;
  const threadActivities = activeThread?.activities ?? EMPTY_THREAD_ACTIVITIES;
  const latestTurnId = activeLatestTurn?.turnId;
  const proposedPlans = activeThread?.proposedPlans;
  // A send the server refuses before it opens a turn is recorded with no turn
  // id, so the work log needs the latest turn's start to tell that refusal from
  // an older one. `requestedAt` covers a turn that was asked for but never
  // started.
  const latestTurnStartedAt = activeLatestTurn?.startedAt ?? activeLatestTurn?.requestedAt ?? null;
  const workLogEntries = useMemo(
    () => deriveWorkLogEntries(threadActivities, latestTurnId ?? undefined, latestTurnStartedAt),
    [latestTurnId, latestTurnStartedAt, threadActivities],
  );
  const latestTurnHasToolActivity = useMemo(
    () => hasToolActivityForTurn(threadActivities, latestTurnId),
    [latestTurnId, threadActivities],
  );
  const pendingApprovals = useMemo(
    () => derivePendingApprovals(threadActivities),
    [threadActivities],
  );
  const pendingUserInputs = useMemo(
    () => derivePendingUserInputs(threadActivities),
    [threadActivities],
  );
  const activeProposedPlan = useMemo(() => {
    if (phase === "running") {
      return null;
    }
    return findLatestProposedPlan(proposedPlans ?? [], latestTurnId ?? null);
  }, [latestTurnId, phase, proposedPlans]);
  const sidebarProposedPlan = useMemo(
    () =>
      findSidebarProposedPlan({
        threads: input.threadPlanCatalog,
        latestTurn: activeLatestTurn,
        latestTurnSettled,
        threadId: activeThread?.id ?? null,
      }),
    [activeLatestTurn, activeThread?.id, latestTurnSettled, input.threadPlanCatalog],
  );
  const activePlan = useMemo(
    () => deriveActivePlanState(threadActivities, latestTurnId ?? undefined),
    [latestTurnId, threadActivities],
  );
  const planSidebarLabel = sidebarProposedPlan || interactionMode === "plan" ? "Plan" : "Tasks";
  const showPlanFollowUpPrompt =
    pendingUserInputs.length === 0 &&
    interactionMode === "plan" &&
    phase !== "running" &&
    hasActionableProposedPlan(activeProposedPlan);
  const activePendingApproval = pendingApprovals[0] ?? null;
  const activeActionRequired = useMemo(
    () => deriveActiveActionRequired(threadActivities, latestTurnId ?? undefined),
    [latestTurnId, threadActivities],
  );
  return {
    threadActivities,
    workLogEntries,
    latestTurnHasToolActivity,
    pendingApprovals,
    pendingUserInputs,
    activeProposedPlan,
    sidebarProposedPlan,
    activePlan,
    planSidebarLabel,
    showPlanFollowUpPrompt,
    activePendingApproval,
    activeActionRequired,
  };
}
